import { useMemo, useState } from "react";

import Drawer from "../../../components/Drawer/Drawer";
import { splitJobsByStatus } from "../../../handlers/jobStatus";
import JobListingItem, { JobListingItemJob } from "../../JobsPage/components/JobListingItem";
import ViewJob from "../../StaticPages/HomePage/components/ViewJob";
import RecentlyClosedJobs from "./RecentlyClosedJobs";

type CompanyJob = JobListingItemJob & {
	jobStatus?: string;
};

interface CompanyJobsProps {
	jobs: CompanyJob[];
	logo?: string;
}

function CompanyJobs({ jobs, logo }: CompanyJobsProps) {
	const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
	const [selectedJob, setSelectedJob] = useState(null);

	const { openJobs, closedJobs } = useMemo(() => splitJobsByStatus(jobs ?? []), [jobs]);

	const viewJob = (job: CompanyJob) => {
		setSelectedJob(job);
		setShowJobDetailsModal(true);
	};

	return (
		<>
			<Drawer
				isOpen={showJobDetailsModal}
				onClose={() => {
					setShowJobDetailsModal(false);
				}}
			>
				<ViewJob data={selectedJob} setData={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
			</Drawer>
			<div className="mt-4">
				<div className="pt-6">
					<h2 className="text-2xl font-semibold mb-4 font-plus-jakarta">
						{openJobs.length === 0 ? "No Open Positions Available" : "Open Positions"}
					</h2>
					{openJobs.length === 0 && closedJobs.length > 0 && (
						<p className="text-sm text-gray-600">
							This company is not hiring right now. These are the roles it closed most recently.
						</p>
					)}
					<div className="">
						{openJobs.map((job) => (
							<JobListingItem
								key={job._id}
								job={{
									...job,
									logo: logo,
								}}
								showCompany={false}
								onClick={() => viewJob(job)}
								onApply={() => viewJob(job)}
								titleClassName="text-sm font-semibold"
							/>
						))}
					</div>
				</div>

				{/* Collapsed while the company has open roles, since closed ones are secondary to those.
				    Open when it has none, where it is the only content this tab carries. */}
				<RecentlyClosedJobs
					jobs={closedJobs}
					defaultOpen={openJobs.length === 0}
					className="pt-8"
					renderJob={(job) => (
						<JobListingItem
							key={job._id}
							job={{
								...job,
								logo: logo,
							}}
							showCompany={false}
							onClick={() => viewJob(job)}
							onApply={() => viewJob(job)}
							titleClassName="text-sm font-semibold"
							// Closed roles are read-only: no saving, just the job details.
							hideSaveButton
							ctaLabel="View"
							ctaVariant="secondary"
							statusBadge="Closed"
						/>
					)}
				/>
			</div>
		</>
	);
}

export default CompanyJobs;
